import { addDays, addMonths, addWeeks, addYears, isAfter, isBefore } from 'date-fns';
import type { CampaignType } from '../types/campaigns.types';

const RECURRING_FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Yearly'] as const;

type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number];

const normalizeFrequency = (value: string | null | undefined): RecurringFrequency | null => {
  if (!value?.trim()) return null;
  const match = RECURRING_FREQUENCIES.find(
    (item) => item.toLowerCase() === value.trim().toLowerCase()
  );
  return match ?? null;
};

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value?.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const applyScheduleTime = (baseDate: Date, scheduleTime: string | null | undefined): Date => {
  const schedule = parseDate(scheduleTime);
  const result = new Date(baseDate);

  if (schedule) {
    result.setHours(
      schedule.getHours(),
      schedule.getMinutes(),
      schedule.getSeconds(),
      schedule.getMilliseconds()
    );
    return result;
  }

  return result;
};

const advanceByFrequency = (date: Date, frequency: RecurringFrequency): Date => {
  switch (frequency) {
    case 'Daily':
      return addDays(date, 1);
    case 'Weekly':
      return addWeeks(date, 1);
    case 'Monthly':
      return addMonths(date, 1);
    case 'Yearly':
      return addYears(date, 1);
    default:
      return addDays(date, 1);
  }
};

export type NextSendDateParams = {
  frequency: string | null | undefined;
  startDate: string | null | undefined;
  scheduleTime?: string | null;
  endDate?: string | null;
  now?: Date;
};

/**
 * Next send for a recurring campaign from frequency + start (+ optional schedule time / end).
 * Uses StartDate's calendar pattern (weekday / day-of-month / anniversary) since finer
 * recurrence rules are not persisted yet.
 */
export const getNextRecurringSendDate = ({
  frequency,
  startDate,
  scheduleTime,
  endDate,
  now = new Date(),
}: NextSendDateParams): Date | null => {
  const normalizedFrequency = normalizeFrequency(frequency);
  const start = parseDate(startDate);
  if (!normalizedFrequency || !start) return null;

  const end = parseDate(endDate);
  let candidate = applyScheduleTime(start, scheduleTime);

  // Guard against pathological loops if dates are invalid after advances
  for (let i = 0; i < 500; i += 1) {
    if (end && isAfter(candidate, end)) {
      return null;
    }

    if (!isBefore(candidate, now)) {
      return candidate;
    }

    candidate = applyScheduleTime(advanceByFrequency(candidate, normalizedFrequency), scheduleTime);
  }

  return null;
};

/** One-time scheduled send: prefer ScheduleTime, else StartDate (+ schedule time-of-day). */
export const getNextScheduledSendDate = ({
  startDate,
  scheduleTime,
}: Pick<NextSendDateParams, 'startDate' | 'scheduleTime'>): Date | null => {
  const schedule = parseDate(scheduleTime);
  if (schedule) return schedule;

  const start = parseDate(startDate);
  if (!start) return null;

  return applyScheduleTime(start, scheduleTime);
};

export type NextCampaignSendDateParams = NextSendDateParams & {
  type: CampaignType | string | null | undefined;
};

export const getNextCampaignSendDate = ({
  type,
  ...params
}: NextCampaignSendDateParams): Date | null => {
  const normalizedType = type?.trim().toLowerCase();

  if (normalizedType === 'recurring') {
    return getNextRecurringSendDate(params);
  }

  if (normalizedType === 'scheduled') {
    return getNextScheduledSendDate(params);
  }

  return null;
};
